import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Bell, Image, Activity, TrendingUp, Clock, CheckCircle, XCircle, MapPin } from 'lucide-react'
import { adminAPI } from '../services/api'

const Dashboard = () => {
  const navigate = useNavigate()
  const [stats, setStats] = useState({
    push: { total: 0, pending: 0, sent: 0, failed: 0 },
    banners: { total: 0, active: 0, inactive: 0 },
    users: { total: 0, synced: 0, unsynced: 0 },
    syncStats: {
      total_operations: "0",
      total_users_processed: "0", 
      total_successful: "0",
      total_errors: "0",
      avg_duration_ms: "0",
      last_sync: null
    }
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchStats()
  }, [])

  const fetchStats = async () => {
    try {
      setLoading(true)
      
      // Загружаем общую статистику, статистику синхронизации и статистику регионов параллельно
      const [generalStatsResponse, syncStatsResponse, regionStatsResponse] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getSyncStats(),
        adminAPI.getRegionStats()
      ])
      
      const newStats = {
        push: { total: 0, pending: 0, sent: 0, failed: 0 },
        banners: { total: 0, active: 0, inactive: 0 },
        users: { total: 0, synced: 0, unsynced: 0 },
        regions: { total: 0, active: 0, inactive: 0 },
        syncStats: {
          total_operations: "0",
          total_users_processed: "0", 
          total_successful: "0",
          total_errors: "0",
          avg_duration_ms: "0",
          last_sync: null
        }
      }
      
      // Обрабатываем общую статистику
      if (generalStatsResponse.data.success) {
        const generalStats = generalStatsResponse.data.stats
        newStats.push = generalStats.push || { total: 0, pending: 0, sent: 0, failed: 0 }
        newStats.banners = generalStats.banners || { total: 0, active: 0, inactive: 0 }
        newStats.users = generalStats.users || { total: 0, synced: 0, unsynced: 0 }
      }
      
      // Обрабатываем статистику синхронизации
      if (syncStatsResponse.data.success) {
        newStats.syncStats = syncStatsResponse.data.stats
      }
      
      // Обрабатываем статистику регионов
      if (regionStatsResponse.data.success) {
        newStats.regions = regionStatsResponse.data.stats
      }
      
      setStats(newStats)
    } catch (error) {
      console.error('Error fetching stats:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Ніколи'
    return new Date(dateString).toLocaleDateString('uk-UA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDuration = (ms) => {
    if (!ms || ms < 1000) return `${Math.round(ms || 0)}мс`
    return `${(ms / 1000).toFixed(1)}с`
  }

  const getSyncStatusBadge = (lastSync) => {
    if (!lastSync) return 'bg-gray-100 text-gray-800'
    
    const now = new Date()
    const syncTime = new Date(lastSync)
    const diffHours = (now - syncTime) / (1000 * 60 * 60)
    
    if (diffHours < 1) return 'bg-green-100 text-green-800'
    if (diffHours < 24) return 'bg-yellow-100 text-yellow-800'
    return 'bg-red-100 text-red-800'
  }

  const getSyncStatusText = (lastSync) => {
    if (!lastSync) return 'Ніколи'
    
    const now = new Date()
    const syncTime = new Date(lastSync)
    const diffHours = (now - syncTime) / (1000 * 60 * 60)
    
    if (diffHours < 1) return 'Нещодавно'
    if (diffHours < 24) return 'Сьогодні'
    return 'Давно'
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Дашборд</h1>
        <p className="text-gray-600">Огляд статистики системи</p>
      </div>

      {/* Основні метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Користувачі</p>
              <p className="text-2xl font-bold text-gray-900">{stats.users.total?.toLocaleString() || 0}</p>
              <p className="text-xs text-green-600">
                {stats.users.synced?.toLocaleString() || 0} синхронізовані
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Bell className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Push-повідомлення</p>
              <p className="text-2xl font-bold text-gray-900">{stats.push.total || 0}</p>
              <p className="text-xs text-yellow-600">
                {stats.push.pending || 0} очікують
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Image className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Баннери</p>
              <p className="text-2xl font-bold text-gray-900">{stats.banners.total || 0}</p>
              <p className="text-xs text-green-600">
                {stats.banners.active || 0} активні
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Activity className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Синхронізація</p>
              <p className="text-2xl font-bold text-gray-900">{stats.syncStats.total_operations || 0}</p>
              <p className="text-xs text-blue-600">
                {formatDuration(parseFloat(stats.syncStats.avg_duration_ms || 0))} середній час
              </p>
            </div>
          </div>
        </div>

        
      </div>

      {/* Детальна статистика */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Статистика користувачів */}
        <div className="card p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Users className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Користувачі</h3>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Всього користувачів</span>
              <span className="font-semibold">{stats.users.total?.toLocaleString() || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Синхронізовані</span>
              <span className="font-semibold text-green-600">{stats.users.synced?.toLocaleString() || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Не синхронізовані</span>
              <span className="font-semibold text-yellow-600">{stats.users.unsynced?.toLocaleString() || 0}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-600 h-2 rounded-full" 
                style={{ 
                  width: `${(stats.users.total || 0) > 0 ? ((stats.users.synced || 0) / (stats.users.total || 1)) * 100 : 0}%` 
                }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 text-center">
              {(stats.users.total || 0) > 0 ? (((stats.users.synced || 0) / (stats.users.total || 1)) * 100).toFixed(1) : 0}% синхронізованих користувачів
            </p>
          </div>
        </div>

        {/* Статистика push-повідомлень */}
        <div className="card p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Bell className="h-5 w-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">Push-повідомлення</h3>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Всього повідомлень</span>
              <span className="font-semibold">{stats.push.total || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Відправлено</span>
              <span className="font-semibold text-green-600">{stats.push.sent || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Очікують</span>
              <span className="font-semibold text-yellow-600">{stats.push.pending || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Помилки</span>
              <span className="font-semibold text-red-600">{stats.push.failed || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Статистика синхронізації з 1С */}
      <div className="card p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Activity className="h-5 w-5 text-orange-600" />
          <h3 className="text-lg font-semibold text-gray-900">Синхронізація з 1С</h3>
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSyncStatusBadge(stats.syncStats.last_sync)}`}>
            {getSyncStatusText(stats.syncStats.last_sync)}
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.syncStats.total_operations || 0}</p>
            <p className="text-sm text-gray-600">Операцій</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">{parseInt(stats.syncStats.total_users_processed || 0).toLocaleString()}</p>
            <p className="text-sm text-gray-600">Користувачів оброблено</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{parseInt(stats.syncStats.total_successful || 0).toLocaleString()}</p>
            <p className="text-sm text-gray-600">Успішних</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600">{parseInt(stats.syncStats.total_errors || 0).toLocaleString()}</p>
            <p className="text-sm text-gray-600">Помилок</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-purple-600">{formatDuration(parseFloat(stats.syncStats.avg_duration_ms || 0))}</p>
            <p className="text-sm text-gray-600">Середній час</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Остання синхронізація</p>
            <p className="text-xs text-gray-500">{formatDate(stats.syncStats.last_sync)}</p>
          </div>
        </div>

        {/* Прогрес бари */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Ефективність синхронізації</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Успішність:</span>
                <span className="font-medium">
                  {parseInt(stats.syncStats.total_users_processed || 0) > 0 
                    ? ((parseInt(stats.syncStats.total_successful || 0) / parseInt(stats.syncStats.total_users_processed || 1)) * 100).toFixed(1)
                    : 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full" 
                  style={{ 
                    width: `${parseInt(stats.syncStats.total_users_processed || 0) > 0 
                      ? ((parseInt(stats.syncStats.total_successful || 0) / parseInt(stats.syncStats.total_users_processed || 1)) * 100)
                      : 0}%` 
                  }}
                ></div>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Статистика помилок</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Помилки:</span>
                <span className="font-medium">
                  {parseInt(stats.syncStats.total_users_processed || 0) > 0 
                    ? ((parseInt(stats.syncStats.total_errors || 0) / parseInt(stats.syncStats.total_users_processed || 1)) * 100).toFixed(1)
                    : 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-red-600 h-2 rounded-full" 
                  style={{ 
                    width: `${parseInt(stats.syncStats.total_users_processed || 0) > 0 
                      ? ((parseInt(stats.syncStats.total_errors || 0) / parseInt(stats.syncStats.total_users_processed || 1)) * 100)
                      : 0}%` 
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Швидкі дії */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Швидкі дії</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button 
            onClick={() => navigate('/notifications')}
            className="btn btn-primary flex items-center justify-center space-x-2"
          >
            <Bell size={20} />
            <span>Створити повідомлення</span>
          </button>
          <button 
            onClick={() => navigate('/banners')}
            className="btn btn-secondary flex items-center justify-center space-x-2"
          >
            <Image size={20} />
            <span>Додати баннер</span>
          </button>
          <button 
            onClick={() => navigate('/users')}
            className="btn btn-secondary flex items-center justify-center space-x-2"
          >
            <Users size={20} />
            <span>Переглянути користувачів</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
